<template>
  <v-dialog :value="value" max-width="960" persistent @input="$emit('input', $event)">
    <v-card :loading="loading">
      <v-card-title class="headline">
        {{ dialogTitle }}
      </v-card-title>
      <v-card-text>
        <v-row v-if="product" dense class="mb-2">
          <v-col cols="12">
            <div class="font-weight-medium">{{ product.display_name }}</div>
            <div class="text-caption grey--text">{{ product.code }}</div>
          </v-col>
        </v-row>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="form.quantity"
              type="number"
              min="0"
              step="0.0001"
              :label="$t('STOCK.RECEIVING_SESSION.LINE_QTY') + '*'"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.lot_number"
              :label="$t('STOCK.RECEIPTS.LOT_NUMBER') + '*'"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="form.gross_weight_kg"
              type="number"
              min="0"
              step="0.0001"
              suffix="kg"
              :label="$t('STOCK.COMMON.GROSS_WEIGHT_KG')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.external_lot_number"
              :label="$t('STOCK.RECEIVING_SESSION.EXTERNAL_LOT')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.pallet_number"
              :label="$t('STOCK.RECEIVING_SESSION.PALLET_NUMBER')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.production_date"
              type="date"
              :label="$t('STOCK.RECEIVING_SESSION.PRODUCTION_DATE')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.expiry_date"
              type="date"
              :label="$t('STOCK.RECEIPTS.EXPIRY') + '*'"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="form.quality_status"
              :items="qualityStatusOptions"
              item-value="value"
              item-text="text"
              :label="$t('STOCK.RECEIPTS.QUALITY_STATUS')"
              dense
              outlined
              hide-details="auto"
              @change="onQualityStatusChange"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="form.quality_hold_reason"
              :label="$t('STOCK.RECEIPTS.QUALITY_HOLD_REASON')"
              :disabled="form.quality_status !== 'blocked'"
              :rules="qualityReasonRules"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="form.unit_cost_net"
              type="number"
              min="0"
              step="0.0001"
              :label="$t('STOCK.RECEIVING_SESSION.UNIT_COST')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="form.vat_rate"
              type="number"
              min="0"
              step="0.01"
              :label="$t('STOCK.RECEIVING_SESSION.VAT_RATE')"
              dense
              outlined
              hide-details="auto"
            />
          </v-col>
        </v-row>

        <v-subheader class="px-0 mt-2 font-weight-bold">
          {{ $t("STOCK.RECEIVING_SESSION.PO_ALLOCATION") }}
        </v-subheader>

        <v-simple-table dense>
          <thead>
            <tr>
              <th>{{ $t("STOCK.RECEIPTS.PURCHASE_ORDER") }}</th>
              <th>{{ $t("STOCK.RECEIVING_SESSION.PO_REMAINING") }}</th>
              <th>{{ $t("STOCK.RECEIVING_SESSION.ALLOCATE_QTY") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in openPoLines" :key="row.purchase_order_item_id">
              <td>{{ row.purchase_order_code }} · #{{ row.line_no }}</td>
              <td>{{ formatQty(row.remaining_quantity) }}</td>
              <td style="max-width: 140px">
                <v-text-field
                  v-model.number="allocations[row.purchase_order_item_id]"
                  type="number"
                  min="0"
                  step="0.0001"
                  dense
                  outlined
                  hide-details
                  @input="onAllocationInput"
                />
              </td>
            </tr>
          </tbody>
        </v-simple-table>

        <v-alert
          :type="allocationValid ? 'success' : 'warning'"
          dense
          text
          class="mt-3 mb-0"
        >
          {{ allocationSummary }}
        </v-alert>

        <v-alert v-if="errorMessage" type="error" dense text class="mt-2 mb-0">
          {{ errorMessage }}
        </v-alert>
      </v-card-text>
      <v-card-actions>
        <v-spacer />
        <v-btn text @click="close">{{ $t("GENERAL.LABELS.CANCEL") }}</v-btn>
        <v-btn color="primary" :disabled="!canSubmit" :loading="submitting" @click="submit">
          {{ $t("STOCK.RECEIVING_SESSION.ACCEPT_ALLOCATION") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  GET_STOCK_RECEIVING_SESSION_PRODUCT_LINES,
  ACCEPT_STOCK_RECEIVING_LOT_ENTRY,
} from "@/store/stock/stockReceivingSessions.module";

const emptyForm = () => ({
  quantity: null,
  gross_weight_kg: null,
  lot_number: "",
  external_lot_number: "",
  pallet_number: "",
  production_date: "",
  expiry_date: "",
  quality_status: "free",
  quality_hold_reason: "",
  unit_cost_net: null,
  vat_rate: null,
});

export default {
  name: "StockReceivingLotAllocationDialog",
  props: {
    value: { type: Boolean, default: false },
    receiptId: { type: [Number, String], required: true },
    product: { type: Object, default: null },
    editEntry: { type: Object, default: null },
    defaultVatRate: { type: Number, default: 20 },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      openPoLines: [],
      allocations: {},
      form: emptyForm(),
      errorMessage: "",
    };
  },
  computed: {
    dialogTitle() {
      return this.editEntry
        ? this.$t("STOCK.RECEIVING_SESSION.EDIT_LOT_ENTRY")
        : this.$t("STOCK.RECEIVING_SESSION.ADD_LOT_ENTRY");
    },
    allocatedSum() {
      return Object.values(this.allocations).reduce(
        (sum, value) => sum + Number(value || 0),
        0
      );
    },
    allocationValid() {
      const qty = Number(this.form.quantity || 0);
      if (qty <= 0) {
        return false;
      }
      return Math.abs(this.allocatedSum - qty) < 0.0001;
    },
    allocationSummary() {
      const qty = Number(this.form.quantity || 0);
      const remaining = qty - this.allocatedSum;
      return this.$t("STOCK.RECEIVING_SESSION.ALLOCATION_REMAINING", {
        remaining: this.formatQty(remaining),
        total: this.formatQty(qty),
      });
    },
    canSubmit() {
      return (
        this.product
        && this.form.lot_number
        && this.form.expiry_date
        && Number(this.form.quantity) > 0
        && this.allocationValid
        && !this.submitting
        && (
          this.form.quality_status !== "blocked"
          || String(this.form.quality_hold_reason || "").trim().length > 0
        )
      );
    },
    qualityStatusOptions() {
      return [
        { value: "free", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_FREE") },
        { value: "blocked", text: this.$t("STOCK.RECEIPTS.QUALITY_STATUS_BLOCKED") },
      ];
    },
    qualityReasonRules() {
      return [
        (v) =>
          this.form.quality_status !== "blocked"
          || String(v || "").trim().length > 0
          || this.$t("STOCK.RECEIPTS.QUALITY_HOLD_REASON"),
      ];
    },
  },
  watch: {
    value(open) {
      if (open) {
        this.bootstrap();
      } else {
        this.reset();
      }
    },
  },
  methods: {
    onQualityStatusChange() {
      if (this.form.quality_status !== "blocked") {
        this.form.quality_hold_reason = "";
      }
    },
    formatQty(value) {
      return Number(value || 0).toFixed(4).replace(/\.?0+$/, "");
    },
    reset() {
      this.form = emptyForm();
      this.allocations = {};
      this.openPoLines = [];
      this.errorMessage = "";
    },
    async bootstrap() {
      if (!this.product) {
        return;
      }
      this.loading = true;
      this.errorMessage = "";
      try {
        const data = await this.$store.dispatch(GET_STOCK_RECEIVING_SESSION_PRODUCT_LINES, {
          receiptId: this.receiptId,
          productId: this.product.id,
        });
        this.openPoLines = data.open_po_lines || [];
        this.allocations = {};
        this.openPoLines.forEach((row) => {
          this.$set(this.allocations, row.purchase_order_item_id, 0);
        });

        if (this.editEntry) {
          this.form = {
            quantity: this.editEntry.quantity,
            gross_weight_kg: this.editEntry.gross_weight_kg,
            lot_number: this.editEntry.lot_number || "",
            external_lot_number: this.editEntry.external_lot_number || "",
            pallet_number: this.editEntry.pallet_number || "",
            production_date: this.editEntry.production_date || "",
            expiry_date: this.editEntry.expiry_date || "",
            quality_status: this.editEntry.quality_status === "blocked" ? "blocked" : "free",
            quality_hold_reason: this.editEntry.quality_hold_reason || "",
            unit_cost_net: this.editEntry.unit_cost_net,
            vat_rate: this.editEntry.vat_rate,
          };
          (this.editEntry.allocations || []).forEach((row) => {
            this.$set(this.allocations, row.purchase_order_item_id, row.quantity);
          });
        } else {
          const firstLine = this.openPoLines[0];
          this.form.vat_rate = firstLine?.vat_rate ?? this.defaultVatRate;
          this.form.unit_cost_net = firstLine?.unit_cost_net ?? null;
        }
      } catch (errors) {
        this.errorMessage = (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR");
      } finally {
        this.loading = false;
      }
    },
    onAllocationInput() {
      this.errorMessage = "";
    },
    close() {
      this.$emit("input", false);
    },
    async submit() {
      if (!this.canSubmit) {
        return;
      }
      this.submitting = true;
      this.errorMessage = "";
      try {
        const allocationPayload = Object.entries(this.allocations)
          .map(([purchaseOrderItemId, quantity]) => ({
            purchase_order_item_id: Number(purchaseOrderItemId),
            quantity: Number(quantity || 0),
          }))
          .filter((row) => row.quantity > 0);

        const params = {
          stock_product_id: this.product.id,
          quantity: Number(this.form.quantity),
          gross_weight_kg: this.form.gross_weight_kg,
          unit_id: this.openPoLines[0]?.unit_id || this.product.primary_unit?.id,
          unit_cost_net: this.form.unit_cost_net,
          vat_rate: this.form.vat_rate,
          lot_number: this.form.lot_number,
          external_lot_number: this.form.external_lot_number || null,
          pallet_number: this.form.pallet_number || null,
          production_date: this.form.production_date || null,
          expiry_date: this.form.expiry_date,
          quality_status: this.form.quality_status === "blocked" ? "blocked" : "free",
          quality_hold_reason:
            this.form.quality_status === "blocked"
              ? this.form.quality_hold_reason || null
              : null,
          allocations: allocationPayload,
        };

        if (this.editEntry?.session_lot_entry_key) {
          params.session_lot_entry_key = this.editEntry.session_lot_entry_key;
        }

        const data = await this.$store.dispatch(ACCEPT_STOCK_RECEIVING_LOT_ENTRY, {
          receiptId: this.receiptId,
          params,
        });
        this.$emit("accepted", data);
        this.close();
      } catch (errors) {
        this.errorMessage = (errors && errors.message) || this.$t("GENERAL.UNEXPECTED_ERROR");
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>
